'use client'

import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { MessageCircle, Send, X, Lock } from 'lucide-react'

type WhatsAppMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
  source?: 'user' | 'whatsapp_human_operator'
}

type WhatsAppHumanSupportWidgetProps = {
  visitorId?: string
  position?: 'top-right' | 'bottom-right' | 'default'
  topOffsetPx?: number
}

function resolveApiBaseUrl() {
  if (typeof window === 'undefined') {
    return 'https://api.authflow.net'
  }

  const configuredUrl = process.env.NEXT_PUBLIC_AUTHFLOW_API_URL
  if (configuredUrl && configuredUrl.trim()) {
    return configuredUrl.trim()
  }

  const hostname = window.location.hostname
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3001'
  }

  return 'https://api.authflow.net'
}

function createFallbackVisitorId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `wa_${crypto.randomUUID()}`
  }
  return `wa_${Date.now()}_${Math.random().toString(16).slice(2)}`
}

function getOperatorTokenFromUrl(): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  const params = new URLSearchParams(window.location.search)
  return params.get('operatorToken')
}

function getTargetVisitorIdFromUrl(): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  const params = new URLSearchParams(window.location.search)
  return params.get('targetVisitorId')
}

function resolveWebSocketUrl() {
  if (typeof window === 'undefined') {
    return 'wss://api.authflow.net/org/get-started/ws'
  }

  const configuredUrl = process.env.NEXT_PUBLIC_WHATSAPP_WS_URL
  if (configuredUrl && configuredUrl.trim()) {
    const trimmed = configuredUrl.trim().replace(/\/$/, '')
    if (trimmed.startsWith('ws://') || trimmed.startsWith('wss://')) {
      return trimmed
    }
    if (trimmed.startsWith('http://')) {
      return trimmed.replace(/^http:/, 'ws:') + '/org/get-started/ws'
    }
    if (trimmed.startsWith('https://')) {
      return trimmed.replace(/^https:/, 'wss:') + '/org/get-started/ws'
    }
    return `wss://${trimmed}/org/get-started/ws`
  }

  // For local development, use chatend on port 4001
  const hostname = window.location.hostname
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'ws://localhost:4001/org/get-started/ws'
  }

  return 'wss://chat.authflow.net/org/get-started/ws'
}

export function WhatsAppHumanSupportWidget({ visitorId, position = 'default', topOffsetPx = 0 }: WhatsAppHumanSupportWidgetProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [input, setInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [resolvedVisitorId, setResolvedVisitorId] = useState(visitorId ?? '')
  const [operatorMode, setOperatorMode] = useState(false)
  const [operatorToken, setOperatorToken] = useState('')
  const [tokenInput, setTokenInput] = useState('')
  const [showTokenInput, setShowTokenInput] = useState(false)
  const [targetVisitorId, setTargetVisitorId] = useState('')
  const [targetVisitorIdInput, setTargetVisitorIdInput] = useState('')
  const [showVisitorIdInput, setShowVisitorIdInput] = useState(false)
  const [messages, setMessages] = useState<WhatsAppMessage[]>([
    {
      id: 'wa_seed',
      role: 'assistant',
      content: 'Ping a human for help right now!',
    },
  ])
  const messageIdsRef = useRef<Set<string>>(new Set(['wa_seed']))
  const socketRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<number | null>(null)
  const [wsStatus, setWsStatus] = useState<'connecting' | 'open' | 'closed' | 'error'>('closed')
  const messageContainerRef = useRef<HTMLDivElement | null>(null)

  const offsetPx = Math.max(0, topOffsetPx)
  const positionClass = position === 'top-right'
    ? 'fixed right-4'
    : position === 'bottom-right'
      ? 'fixed right-4'
      : ''
  const positionStyle = position === 'top-right'
    ? { top: `${16 + offsetPx}px` }
    : position === 'bottom-right'
      ? { bottom: `${16 + offsetPx}px` }
      : undefined

  useEffect(() => {
    if (visitorId && visitorId.trim()) {
      setResolvedVisitorId(visitorId.trim())
      return
    }

    if (typeof window === 'undefined') {
      return
    }

    const storageKey = 'authflow_whatsapp_visitor_id'
    const stored = window.localStorage.getItem(storageKey)
    if (stored && stored.trim()) {
      setResolvedVisitorId(stored.trim())
      return
    }

    const generated = createFallbackVisitorId()
    window.localStorage.setItem(storageKey, generated)
    setResolvedVisitorId(generated)
  }, [visitorId])

  // Check for operator token and target visitor ID in URL or localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const urlToken = getOperatorTokenFromUrl()
    const urlTargetVisitorId = getTargetVisitorIdFromUrl()
    
    if (urlToken) {
      setOperatorToken(urlToken)
      setOperatorMode(true)
      window.localStorage.setItem('authflow_whatsapp_operator_token', urlToken)
    } else {
      const storedToken = window.localStorage.getItem('authflow_whatsapp_operator_token')
      if (storedToken) {
        setOperatorToken(storedToken)
        setOperatorMode(true)
      }
    }

    if (urlTargetVisitorId) {
      setTargetVisitorId(urlTargetVisitorId)
      window.localStorage.setItem('authflow_whatsapp_target_visitor_id', urlTargetVisitorId)
    } else {
      const storedTargetId = window.localStorage.getItem('authflow_whatsapp_target_visitor_id')
      if (storedTargetId) {
        setTargetVisitorId(storedTargetId)
      }
    }
  }, [])

  useEffect(() => {
    const connectWebSocket = () => {
      if (typeof window === 'undefined') {
        return
      }

      const visitorIdForConnection = resolvedVisitorId
      if (!visitorIdForConnection) {
        return
      }

      if (operatorMode && !operatorToken) {
        return
      }

      const wsUrl = resolveWebSocketUrl()
      const socket = new WebSocket(wsUrl)
      socketRef.current = socket
      setWsStatus('connecting')

      socket.onopen = () => {
        setWsStatus('open')
        const initPayload = {
          type: 'init',
          role: operatorMode ? 'operator' : 'visitor',
          visitorId: visitorIdForConnection,
          pageUrl: typeof window !== 'undefined' ? window.location.href : null,
          operatorToken: operatorMode ? operatorToken : undefined,
          targetVisitorId: operatorMode ? targetVisitorId || '' : undefined,
        }
        socket.send(JSON.stringify(initPayload))
      }

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data.toString()) as {
            type: string
            messages?: Array<{ id?: string; content?: string; source?: string }>
            message?: string
          }

          if (payload.type === 'message' || payload.type === 'history') {
            const incoming = (payload.messages || [])
              .filter((message) => typeof message.content === 'string' && message.content.trim().length > 0)
              .map((message) => ({
                id: message.id || `wa_in_${Date.now()}_${Math.random().toString(16).slice(2)}`,
                role: 'assistant' as const,
                source: message.source as 'whatsapp_human_operator' | undefined,
                content: String(message.content).trim(),
              }))
              .filter((message) => !messageIdsRef.current.has(message.id))

            if (incoming.length > 0) {
              incoming.forEach((message) => messageIdsRef.current.add(message.id))
              setMessages((prev) => [...prev, ...incoming])
            }
            return
          }

          if (payload.type === 'error' && payload.message) {
            const errorId = `wa_error_${Date.now()}`
            const messageText = String(payload.message)
            if (!messageIdsRef.current.has(errorId)) {
              messageIdsRef.current.add(errorId)
              setMessages((prev) => [
                ...prev,
                {
                  id: errorId,
                  role: 'assistant',
                  content: messageText,
                },
              ])
            }
            return
          }
        } catch (err) {
          // ignore invalid ws payloads
        }
      }

      socket.onclose = () => {
        setWsStatus('closed')
        if (reconnectTimerRef.current) {
          window.clearTimeout(reconnectTimerRef.current)
        }
        reconnectTimerRef.current = window.setTimeout(() => {
          connectWebSocket()
        }, 3000)
      }

      socket.onerror = () => {
        setWsStatus('error')
      }
    }

    connectWebSocket()

    return () => {
      if (reconnectTimerRef.current) {
        window.clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
      if (socketRef.current) {
        socketRef.current.close()
        socketRef.current = null
      }
    }
  }, [resolvedVisitorId, operatorMode, operatorToken])

  useEffect(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN && operatorMode) {
      const payload = {
        type: 'set_target',
        targetVisitorId: targetVisitorId || '',
      }
      socketRef.current.send(JSON.stringify(payload))
    }
  }, [operatorMode, targetVisitorId])

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (!messageContainerRef.current) return
    // ensure DOM updated
    window.requestAnimationFrame(() => {
      const el = messageContainerRef.current
      if (el) {
        el.scrollTop = el.scrollHeight
      }
    })
  }, [messages])

  const sendMessage = async () => {
    // For operators, check targetVisitorId; for visitors, check resolvedVisitorId
    const visitorIdToUse = operatorMode ? targetVisitorId : resolvedVisitorId
    
    if (!input.trim() || !visitorIdToUse || isSending) {
      return
    }

    const content = input.trim()
    const userMessageId = operatorMode ? `wa_op_${Date.now()}` : `wa_${Date.now()}`
    const apiBaseUrl = resolveApiBaseUrl()

    setInput('')
    setIsSending(true)
    messageIdsRef.current.add(userMessageId)

    // Show message immediately for operators (no user/assistant distinction)
    if (operatorMode) {
      setMessages((prev) => [
        ...prev,
        {
          id: userMessageId,
          role: 'assistant',
          source: 'whatsapp_human_operator',
          content,
        },
      ])
    } else {
      setMessages((prev) => [
        ...prev,
        {
          id: userMessageId,
          role: 'user',
          content,
        },
      ])
    }

    try {
      const socket = socketRef.current
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        throw new Error('WebSocket is not connected')
      }

      const payload = operatorMode
        ? {
            type: 'operator_message',
            content,
          }
        : {
            type: 'visitor_message',
            content,
            pageUrl: typeof window !== 'undefined' ? window.location.href : null,
          }

      socket.send(JSON.stringify(payload))
    } catch (error) {
      const errorId = `wa_error_${Date.now()}`
      messageIdsRef.current.add(errorId)
      setMessages((prev) => [
        ...prev,
        {
          id: errorId,
          role: 'assistant',
          content: 'Unable to send message right now. Please try again.',
        },
      ])
    } finally {
      setIsSending(false)
    }
  }

  const handleSetOperatorToken = (token: string) => {
    setOperatorToken(token)
    setOperatorMode(true)
    window.localStorage.setItem('authflow_whatsapp_operator_token', token)
    setTokenInput('')
    setShowTokenInput(false)
  }

  const handleSetTargetVisitorId = (visitorId: string) => {
    const cleanId = visitorId.trim()
    if (cleanId) {
      setTargetVisitorId(cleanId)
      window.localStorage.setItem('authflow_whatsapp_target_visitor_id', cleanId)
      setTargetVisitorIdInput('')
      setShowVisitorIdInput(false)
      // Clear messages when switching to a different visitor
      setMessages([])
      messageIdsRef.current.clear()
    }
  }

  const handleLogoutOperator = () => {
    setOperatorToken('')
    setOperatorMode(false)
    setTargetVisitorId('')
    window.localStorage.removeItem('authflow_whatsapp_operator_token')
    window.localStorage.removeItem('authflow_whatsapp_target_visitor_id')
  }

  return (
    <div className={`${positionClass} z-50`} style={positionStyle}>
      {!isOpen && (
        <div className="flex items-center gap-2">
          <div className="rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground shadow-lg">
            {operatorMode ? 'Operator Mode' : 'Ping a human for help!'}
          </div>
          <Button
            onClick={() => setIsOpen(true)}
            className="h-14 w-14 rounded-full bg-[#25D366] text-white shadow-lg hover:bg-[#1eb85a]"
            size="icon"
            aria-label={operatorMode ? 'Open WhatsApp operator console' : 'Open WhatsApp support chat'}
          >
            {operatorMode ? <Lock className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
          </Button>
        </div>
      )}

      {isOpen && (
        <Card className="fixed bottom-40 right-6 z-[60] h-[420px] w-96 max-w-[calc(100vw-2rem)] shadow-2xl flex flex-col p-1">
          <div className="flex items-center justify-between border-b px-3 py-1.5 bg-[#25D366] text-white rounded-t-md">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">WhatsApp {operatorMode ? 'Operator' : 'Support'}</h3>
              {operatorMode && <Lock className="h-3 w-3" />}
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 text-white hover:bg-white/20 hover:text-white"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {!operatorMode && showTokenInput ? (
            <CardContent className="flex-1 flex flex-col justify-center px-3 py-2 space-y-3">
              <div>
                <p className="text-sm font-semibold mb-2">Operator Access</p>
                <p className="text-xs text-muted-foreground mb-3">
                  Enter your operator token to send messages directly to visitors.
                </p>
              </div>
              <Input
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                placeholder="Enter operator token"
                type="password"
                disabled={isSending}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handleSetOperatorToken(tokenInput)}
                  disabled={!tokenInput.trim() || isSending}
                  className="flex-1"
                >
                  Activate
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowTokenInput(false)
                    setTokenInput('')
                  }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          ) : operatorMode && showVisitorIdInput ? (
            <CardContent className="flex-1 flex flex-col justify-center px-3 py-2 space-y-3">
              <div>
                <p className="text-sm font-semibold mb-2">Target Visitor</p>
                <p className="text-xs text-muted-foreground mb-3">
                  Enter the visitor ID you want to chat with.
                </p>
              </div>
              <Input
                value={targetVisitorIdInput}
                onChange={(e) => setTargetVisitorIdInput(e.target.value)}
                placeholder="e.g., wa_visitor_123"
                disabled={isSending}
              />
              <div className="text-xs text-muted-foreground">
                Current: {targetVisitorId || 'None selected'}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handleSetTargetVisitorId(targetVisitorIdInput)}
                  disabled={!targetVisitorIdInput.trim() || isSending}
                  className="flex-1"
                >
                  Select
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowVisitorIdInput(false)
                    setTargetVisitorIdInput('')
                  }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          ) : (
            <>
              <CardContent ref={messageContainerRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
                {messages.map((message) => (
                  <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                        message.role === 'user'
                          ? 'bg-[#DCF8C6] text-zinc-900'
                          : message.source === 'whatsapp_human_operator'
                          ? 'bg-[#E7F5FE] text-blue-900 border border-blue-200'
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      {message.content}
                    </div>
                  </div>
                ))}
              </CardContent>

              <div className="border-t px-3 py-2 space-y-2">
                {operatorMode && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowVisitorIdInput(true)}
                      className="w-full text-xs"
                      disabled={isSending}
                    >
                      Change Visitor ({targetVisitorId ? targetVisitorId.slice(0, 12) + '...' : 'None'})
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleLogoutOperator}
                      className="w-full text-xs"
                    >
                      Exit Operator Mode
                    </Button>
                  </>
                )}
                <div className="flex items-center gap-2">
                  <Input
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault()
                        sendMessage()
                      }
                    }}
                    placeholder={operatorMode ? 'Send operator message...' : 'Message human support'}
                    disabled={isSending || (operatorMode ? !targetVisitorId : !resolvedVisitorId)}
                  />
                  <Button
                    size="icon"
                    onClick={sendMessage}
                    disabled={isSending || !input.trim() || (operatorMode ? !targetVisitorId : !resolvedVisitorId)}
                    className="bg-[#25D366] text-white hover:bg-[#1eb85a]"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
                {/* Operator Mode UI hidden from visitors - operator access via URL or developer tools only */}
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  )
}
